import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import {
  FaArrowLeft, FaBan, FaCar, FaCheckCircle, FaClone, FaEdit, FaEnvelope,
  FaList, FaPowerOff, FaTimesCircle, FaTrashAlt,
} from 'react-icons/fa';
import { MdOutlineTransferWithinAStation } from 'react-icons/md';
import { deleteBooking, resetBooking, sendBackEmail, sendBackToApprove } from 'redux/booking/car/carBookingSlice';
import { Card, Button, TwIconButton, ActionDropdown, Confirmation } from 'components/common';
import TransactionDetailApprovalModal from './TransactionDetailApprovalModal';
import TransactionDetailCancelModal from './TransactionDetailCancelModal';
import TransactionDetailRejectModal from './TransactionDetailRejectModal';
import CloneBookingConfirm from './CloneBookingConfirm';
import TransferSiteOfficeConfirm from './TransferSiteOfficeConfirm';

const TransactionDetailHeader = ({ transaction }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmType, setConfirmType] = useState('delete');
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [showCloneConfirm, setShowCloneConfirm] = useState(false);
  const [showTransferSiteOfficeForm, setShowTransferSiteOfficeForm] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const transactionUrl = '/booking/car';
  const transactionId = transaction?.id;

  const {
    allow_create, allow_edit, allow_delete, allow_approve, allow_reject, allow_reset,
    allow_cancel, allow_send_back_email, allow_send_to_approve, allow_clone, allow_transfer_site_office,
  } = transaction?.permissions || {};

  const handleActionConfirm = async () => {
    try {
      if (confirmType === 'delete') {
        dispatch(deleteBooking(transaction));
        navigate(`${transactionUrl}/list`);
      } else if (confirmType === 'reset') {
        dispatch(resetBooking(transactionId));
        setShowConfirm(false);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openConfirm = (type) => { setConfirmType(type); setShowConfirm(true); };

  const dropdownItems = [];
  if (allow_edit) dropdownItems.push({ label: 'Edit', icon: <FaEdit />, onClick: () => navigate(`${transactionUrl}/${transactionId}/edit`, { state: transaction }) });
  if (allow_send_to_approve) dropdownItems.push({ label: 'Send back to approve', icon: <FaEnvelope />, onClick: () => dispatch(sendBackToApprove(transactionId)) });
  if (allow_send_back_email) dropdownItems.push({ label: 'Send back email', icon: <FaEnvelope />, onClick: () => dispatch(sendBackEmail(transactionId)) });
  if (allow_transfer_site_office) dropdownItems.push({ label: 'Transfer site office', icon: <MdOutlineTransferWithinAStation />, onClick: () => setShowTransferSiteOfficeForm(true) });
  if (allow_reset) dropdownItems.push({ divider: true }, { label: 'Reset', icon: <FaPowerOff />, onClick: () => openConfirm('reset') });
  if (allow_cancel) dropdownItems.push({ label: 'Cancel', icon: <FaBan />, danger: true, onClick: () => setShowCancelForm(true) });
  if (allow_delete) dropdownItems.push({ divider: true }, { label: 'Delete', icon: <FaTrashAlt />, danger: true, onClick: () => openConfirm('delete') });

  return (
    <>
      <Card padding="none" className="tw-mb-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
          <TwIconButton variant="secondary" icon={FaArrowLeft} onClick={() => navigate(-1)} title="Back" />

          <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap tw-justify-end">
            {allow_create && (
              <Button size="sm" variant="secondary" leftIcon={FaCar} onClick={() => navigate('/booking/car/new-car-booking')}>
                <span className="tw-hidden xl:tw-inline">New Car Booking</span>
              </Button>
            )}
            {allow_approve && (
              <Button size="sm" variant="success" leftIcon={FaCheckCircle} onClick={() => setShowApproveForm(true)}>
                <span className="tw-hidden xl:tw-inline">Approve</span>
              </Button>
            )}
            {allow_reject && (
              <Button size="sm" variant="danger" leftIcon={FaTimesCircle} onClick={() => setShowRejectForm(true)}>
                <span className="tw-hidden xl:tw-inline">Reject</span>
              </Button>
            )}
            {allow_clone && (
              <Button size="sm" variant="secondary" leftIcon={FaClone} onClick={() => setShowCloneConfirm(true)}>
                <span className="tw-hidden xl:tw-inline">Clone</span>
              </Button>
            )}
            <Button size="sm" variant="secondary" leftIcon={FaList} onClick={() => navigate(`${transactionUrl}/list`)}>
              <span className="tw-hidden xl:tw-inline">List</span>
            </Button>
            {dropdownItems.length > 0 && <ActionDropdown items={dropdownItems} />}
          </div>
        </div>
      </Card>

      <Confirmation
        show={showConfirm}
        type={confirmType === 'delete' ? 'danger' : 'warning'}
        title={confirmType === 'delete' ? 'Delete booking' : 'Reset booking'}
        message={`Are you sure to ${confirmType} booking ID#${transactionId}?`}
        confirmText={confirmType === 'delete' ? 'Delete' : 'Reset'}
        onConfirm={handleActionConfirm}
        onCancel={() => setShowConfirm(false)}
      />

      {showApproveForm && (
        <TransactionDetailApprovalModal showModal={showApproveForm} setshowModal={setShowApproveForm} transaction={transaction} />
      )}
      {showRejectForm && (
        <TransactionDetailRejectModal showModal={showRejectForm} setshowModal={setShowRejectForm} transaction={transaction} />
      )}
      {showCancelForm && (
        <TransactionDetailCancelModal showModal={showCancelForm} setshowModal={setShowCancelForm} transaction={transaction} />
      )}
      {showCloneConfirm && (
        <CloneBookingConfirm showModal={showCloneConfirm} setshowModal={setShowCloneConfirm} transaction={transaction} />
      )}
      {showTransferSiteOfficeForm && (
        <TransferSiteOfficeConfirm showModal={showTransferSiteOfficeForm} setshowModal={setShowTransferSiteOfficeForm} transaction={transaction} />
      )}
    </>
  );
};

TransactionDetailHeader.propTypes = {
  transaction: PropTypes.object.isRequired,
};

export default TransactionDetailHeader;
